import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import LlwinGameChoiceModal from "./LlwinGameChoiceModal";
import { getAssetUrl } from "../utils/assetUrl";
import "./Gg88GameTypeModal.css";

export const GG88_ROUTE_SLOT = "/NH";
export const GG88_ROUTE_BAN_CA = "/casino/lobby";
export const LLWIN_ROUTE_SLOT = "/NH";
export const LLWIN_ROUTE_BAN_CA = "/casino/lobby";

export type Gg88GameTypeModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  isInline?: boolean;
};

interface GameLobbyItem {
  id: string;
  name: string;
  logoText: string;
  percent: string;
  route: string;
  subtitle?: string;
  textColor?: string;
  isPlane?: boolean;
}

const BASE_LOBBY_ITEMS: Omit<GameLobbyItem, "percent">[] = [
  {
    id: "fly88",
    name: "FLY88",
    logoText: "FLY88",
    route: "/NH",
    textColor: "#00E5FF",
    isPlane: true,
  },
  {
    id: "xx88",
    name: "XX88",
    logoText: "XX88",
    route: "/NH",
    textColor: "#00A2FF",
  },
  {
    id: "llwin",
    name: "LLWIN",
    logoText: "LLwin",
    route: "/casino/lobby",
    textColor: "#38B6FF",
  },
  {
    id: "ll88",
    name: "LL88",
    logoText: "LL88",
    route: "/NH",
    textColor: "#00FF88",
  },
  {
    id: "mm88",
    name: "MM88",
    logoText: "MM88",
    route: "/casino/lobby",
    textColor: "#FF3355",
  },
];

const Gg88GameTypeModal: React.FC<Gg88GameTypeModalProps> = ({
  isOpen = true,
  onClose,
  isInline = true,
}) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(2); // LLWIN (index 2) centered
  const [showLlwinChoice, setShowLlwinChoice] = useState(false);

  // Generate random win rate: LLWIN (85% - 95%), others (< 30%)
  const lobbyItems = useMemo<GameLobbyItem[]>(() => {
    return BASE_LOBBY_ITEMS.map((item) => {
      let percentVal: number;
      if (item.id === "llwin") {
        // Random between 85% and 95%
        percentVal = Math.floor(Math.random() * 11) + 85;
      } else {
        // Random under 30% (15% - 28%)
        percentVal = Math.floor(Math.random() * 14) + 15;
      }
      return {
        ...item,
        percent: `${percentVal}%`,
      };
    });
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "ArrowRight") handleNext();
    },
    []
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onKeyDown]);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + lobbyItems.length) % lobbyItems.length);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % lobbyItems.length);
  };

  const handleSelectLobby = (item: GameLobbyItem) => {
    if (item.id === "llwin") {
      setShowLlwinChoice(true);
    } else {
      Swal.fire({
        title: "THÔNG BÁO",
        html: `Sảnh ${item.name} có tỷ lệ khá thấp. Vui lòng chọn sảnh LLWIN để có trải nghiệm tốt nhất!`,
        confirmButtonText: "ĐÃ HIỂU",
        customClass: {
          popup: "custom-swal",
        },
      });
    }
  };

  if (!isOpen) return null;

  // Compute 5 visible items around activeIndex
  const total = lobbyItems.length;
  const visibleIndices = [-2, -1, 0, 1, 2].map(
    (offset) => (activeIndex + offset + total) % total
  );

  return (
    <>
      <section className={`llwin-game-section ${isInline ? "llwin-game-section--inline" : ""}`}>
        <div className="llwin-game-section__container">
          <h1 className="llwin-game-section__title">CHỌN SẢNH GAME</h1>

          <div className="llwin-game-cards-container">
            {visibleIndices.map((itemIdx, posIdx) => {
              const item = lobbyItems[itemIdx];
              const isCenter = posIdx === 2;

              return (
                <div
                  key={`${item.id}-${posIdx}`}
                  className={`llwin-game-card ${isCenter ? "llwin-game-card--active" : ""} llwin-game-card--pos-${posIdx}`}
                  onClick={() => (isCenter ? handleSelectLobby(item) : setActiveIndex(itemIdx))}
                  style={{
                    backgroundImage: `url(${getAssetUrl("/assets/bg-card-sexy.png")})`,
                  }}
                >
                  <div className="llwin-game-card__inner">
                    <div className="llwin-game-card__logo-box">
                      {item.isPlane && (
                        <span className="llwin-game-card__plane-icon" aria-hidden>
                          &#x2708;
                        </span>
                      )}
                      <span
                        className="llwin-game-card__logo-text"
                        style={{ color: item.textColor || "#00E5FF" }}
                      >
                        {item.logoText}
                      </span>
                    </div>

                    <h3 className="llwin-game-card__name">{item.name}</h3>

                    <div className="llwin-game-card__badge">
                      <span className="llwin-game-card__badge-percent">{item.percent}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Carousel Navigation Footer */}
          <div className="llwin-game-carousel-nav">
            <button
              type="button"
              className="llwin-game-nav-btn llwin-game-nav-btn--prev"
              onClick={handlePrev}
              aria-label="Sảnh trước"
            >
              <img
                src={getAssetUrl("/assets/btn-back.png")}
                alt="Back Arrow"
                className="llwin-game-nav-arrow-img"
              />
            </button>

            <div className="llwin-game-nav-dots">
              {lobbyItems.map((_, idx) => (
                <span
                  key={idx}
                  className={`llwin-game-nav-dot ${idx === activeIndex ? "llwin-game-nav-dot--active" : ""}`}
                  onClick={() => setActiveIndex(idx)}
                />
              ))}
            </div>

            <button
              type="button"
              className="llwin-game-nav-btn llwin-game-nav-btn--next"
              onClick={handleNext}
              aria-label="Sảnh tiếp theo"
            >
              <img
                src={getAssetUrl("/assets/btn-back.png")}
                alt="Next Arrow"
                className="llwin-game-nav-arrow-img llwin-game-nav-arrow-img--flipped"
              />
            </button>
          </div>
        </div>
      </section>

      <LlwinGameChoiceModal
        isOpen={showLlwinChoice}
        onClose={() => setShowLlwinChoice(false)}
      />
    </>
  );
};

export default Gg88GameTypeModal;
